/**
 * Regulatory Checkup — /checkup
 * Public page (no auth). 3-screen flow: Project → Status → Results.
 * No PII collected. Email gate before full report details.
 */
import { useState, useEffect, useCallback } from "react";
import { ChevronRight, ChevronLeft, FileText, AlertTriangle, CheckCircle, ArrowRight, Shield, TrendingUp, Landmark, Leaf, Zap, Mail, Lock } from "lucide-react";
import { getCheckup } from "../lib/api.ts";
import { supabase } from "../lib/supabase-client.ts";
import { COUNTRY_META } from "../lib/constants.ts";
import { getIntentOptions, getCapacityHint, getCapacityStep, LOCATION_PLACEHOLDER, EIA_TYPE_OPTIONS, LAND_TENURE_OPTIONS, OFFTAKER_OPTIONS, getCountryQuestions } from "../lib/checkup-options.ts";
import type { EvaluationOutput, ComplianceSummary, Nudge } from "../lib/evaluation.ts";

type CountryCode = "KE" | "NG" | "ZA" | "NA" | "ZM" | "GH";
const ALL_COUNTRIES: CountryCode[] = ["KE", "NG", "ZA", "NA", "ZM", "GH"];

/** Detect embed mode from URL param: /checkup?embed=true */
function useEmbedMode() {
  const params = new URLSearchParams(window.location.search);
  const isEmbed = params.get("embed") === "true";

  const postToParent = useCallback((type: string, data?: Record<string, any>) => {
    if (isEmbed && window.parent !== window) {
      // Restrict to enereg.com domains; falls back to "*" only if referrer is unavailable
      const origin = document.referrer ? new URL(document.referrer).origin : "*";
      window.parent.postMessage({ type, ...data }, origin);
    }
  }, [isEmbed]);

  // Send height to parent — debounced, avoids infinite mutation loop
  useEffect(() => {
    if (!isEmbed) return;
    let rafId = 0;
    const observer = new MutationObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(sendHeight);
    });

    function sendHeight() {
      observer.disconnect();
      document.body.style.height = 'auto';
      document.body.style.overflow = 'hidden';
      const height = document.body.scrollHeight;
      document.body.style.height = '';
      document.body.style.overflow = '';
      postToParent("enereg-checkup-resize", { height });
      observer.observe(document.body, { childList: true, subtree: true });
    }

    sendHeight();
    window.addEventListener("resize", sendHeight);
    return () => { cancelAnimationFrame(rafId); observer.disconnect(); window.removeEventListener("resize", sendHeight); };
  }, [isEmbed, postToParent]);

  return { isEmbed, postToParent };
}

interface CheckupState {
  country: CountryCode;
  capacity_kw: number;
  intent: string;
  fields: Record<string, string>;
}

// Map generalized answers to field values the evaluation engine understands
function mapAnswersToFields(state: CheckupState): Record<string, string> {
  const mapped: Record<string, string> = {};

  for (const [id, val] of Object.entries(state.fields)) {
    if (!val || val === "" || val === "Not yet secured" || val === "Not yet determined" || val === "Not started" || val === "Not yet") continue;

    switch (id) {
      case "S-009":
        if (val === "Yes") mapped["S-009"] = "Secured";
        break;
      case "E-005":
        if (val === "Yes, approved") mapped["E-005"] = "Approved";
        break;
      case "G-003":
        if (val === "Yes") mapped["G-003"] = "Connected";
        else if (val === "Applied") mapped["G-003"] = "Applied";
        break;
      case "F-001":
        if (val === "Under $500K") mapped["F-001"] = "250000";
        else if (val === "$500K - $2M") mapped["F-001"] = "1250000";
        else if (val === "$2M - $10M") mapped["F-001"] = "6000000";
        else if (val === "$10M - $50M") mapped["F-001"] = "30000000";
        else if (val === "Over $50M") mapped["F-001"] = "75000000";
        break;
      case "F-006":
        if (val === "Yes, agreed with offtaker") mapped["F-006"] = "Agreed";
        else if (val === "Estimated but not agreed") mapped["F-006"] = "Estimated";
        break;
      case "F-011":
        if (val !== "Not yet determined") mapped["F-011"] = val;
        break;
      default:
        mapped[id] = val;
        break;
    }
  }

  if (state.fields["P-002"]) mapped["P-002"] = state.fields["P-002"];
  mapped["C-001"] = "Company registered";
  mapped["C-002"] = "Registered";

  return mapped;
}

// ─── Print styles ───
const PRINT_STYLES = `
@media print {
  body { background: white !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .print-hide { display: none !important; }
  .print-show { display: block !important; }
  section, .bg-surface { break-inside: avoid; box-shadow: none !important; border-color: #e5e7eb !important; }
  .bg-surface-secondary { background: #f9fafb !important; }
  .bg-brand-light { background: #ecfdf5 !important; }
  .bg-ai-light { background: #f0f0ff !important; }
  @page { margin: 1cm; size: A4; }
}
`;

const STEP_NAMES: string[] = ["Project", "Status", "Results"];
const SKELETON_CARDS = [1, 2, 3, 4] as const;
const SKELETON_ROWS = [1, 2, 3, 4, 5] as const;

export function Checkup() {
  const { isEmbed, postToParent } = useEmbedMode();
  const [step, setStep] = useState(0); // 0=Project, 1=Status, 2=Results
  const [state, setState] = useState<CheckupState>({
    country: "KE",
    capacity_kw: 5000,
    intent: "",
    fields: {},
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<EvaluationOutput | null>(null);
  const [email, setEmail] = useState("");
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [checkupToken, setCheckupToken] = useState<string | null>(null);
  // Notify parent of step changes
  useEffect(() => { postToParent("enereg-checkup-step", { step }); }, [step, postToParent]);

  const stepNames = STEP_NAMES;

  function updateField(fieldId: string, value: string) {
    setState(prev => ({ ...prev, fields: { ...prev.fields, [fieldId]: value } }));
  }

  async function handleGenerate() {
    setLoading(true);
    setError(null);
    setStep(2);

    try {
      const minDelay = new Promise(r => setTimeout(r, 1800));
      const mapped = mapAnswersToFields(state);
      const [evaluation] = await Promise.all([
        getCheckup({
          country_code: state.country,
          capacity_kw: state.capacity_kw,
          fields: mapped,
          intent: state.intent || undefined,
        }),
        minDelay,
      ]);
      setResult(evaluation);
    } catch (e: any) {
      setError(e.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  // Results step
  if (step === 2) {
    if (loading) {
      return (
        <div className={`${isEmbed ? "px-2 py-4" : "max-w-3xl mx-auto px-4 py-8"}`}>
          {!isEmbed && <style dangerouslySetInnerHTML={{ __html: PRINT_STYLES }} />}
          {!isEmbed && <CheckupHeader country={state.country} />}
          {/* Skeleton mimics results layout */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            {SKELETON_CARDS.map(i => (
              <div key={i} className="bg-surface border border-border rounded-xl p-3">
                <div className="skeleton h-3 w-16 mb-2 mx-auto" />
                <div className="skeleton h-5 w-12 mx-auto" />
              </div>
            ))}
          </div>
          <div className="bg-surface border border-border rounded-2xl p-5 mb-4">
            <div className="skeleton h-4 w-40 mb-4" />
            <div className="space-y-2">
              {SKELETON_ROWS.map(i => (
                <div key={i} className="skeleton h-12 rounded-xl" />
              ))}
            </div>
          </div>
          <p className="text-[14px] text-text-tertiary text-center">Analyzing your project against {COUNTRY_META[state.country].name}'s regulatory requirements...</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className={`${isEmbed ? "px-2 py-4" : "max-w-3xl mx-auto px-4 py-8"}`}>
          {!isEmbed && <CheckupHeader country={state.country} />}
          <div className="bg-surface border border-danger/20 rounded-2xl p-6 text-center">
            <AlertTriangle size={24} className="text-danger mx-auto mb-2" />
            <p className="text-[14px] text-danger mb-4">{error}</p>
            <button
              onClick={() => { setStep(0); setError(null); }}
              className="px-4 py-1.5 bg-brand text-white rounded-xl text-[14px] font-medium"
            >
              Try again
            </button>
          </div>
        </div>
      );
    }

    if (result) {
      return (
        <CheckupResults
          result={result}
          state={state}
          email={email}
          emailSubmitted={emailSubmitted}
          onEmailChange={setEmail}
          onEmailSubmit={() => {
            setEmailSubmitted(true);
            postToParent("enereg-checkup-email", { email });
            // Store lead + checkup snapshot in Supabase waitlist, capture row ID as token
            supabase.from("waitlist").insert({
              email: email.trim().toLowerCase(),
              country: state.country,
              capacity: `${(state.capacity_kw / 1000).toFixed(1)}MW`,
              source: "checkup",
              checkup_snapshot: {
                country: state.country,
                capacity_kw: state.capacity_kw,
                intent: state.intent,
                fields: state.fields,
                mapped_fields: mapAnswersToFields(state),
              },
            }).select("id").single().then(({ data }) => {
              if (data?.id) setCheckupToken(data.id);
            });
          }}
          onStartOver={() => { setStep(0); setResult(null); setEmailSubmitted(false); postToParent("enereg-checkup-step", { step: 0 }); }}
          isEmbed={isEmbed}
          postToParent={postToParent}
        />
      );
    }
  }

  return (
    <div className={`${isEmbed ? "px-2 py-4" : "max-w-xl mx-auto px-4 py-8"}`}>
      {!isEmbed && <CheckupHeader country={state.country} />}

      {/* Step indicators */}
      <div className="flex items-center gap-2 mb-8">
        {stepNames.map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <div
              className={`w-7 h-7 rounded-full text-[13px] font-medium flex items-center justify-center ${
                i < step ? "bg-brand text-white" : i === step ? "bg-brand text-white" : "bg-surface-tertiary text-text-tertiary"
              }`}
            >
              {i < step ? <CheckCircle size={14} /> : i + 1}
            </div>
            <span className={`text-[14px] ${i === step ? "text-text-primary font-medium" : "text-text-tertiary"}`}>
              {s}
            </span>
            {i < stepNames.length - 1 && <ChevronRight size={14} className="text-text-tertiary mx-1" />}
          </div>
        ))}
      </div>

      <div className="bg-surface border border-border rounded-2xl p-5">
        {/* Step 0: Your Project */}
        {step === 0 && (
          <div className="space-y-5">
            <CountrySelector
              countries={ALL_COUNTRIES}
              selected={state.country}
              onSelect={(code) => {
                if (code !== state.country) {
                  const { "E-001": _, "S-010": __, ...rest } = state.fields;
                  setState({ ...state, country: code, intent: "", fields: rest });
                }
              }}
            />
            <div>
              <label className="block text-[13px] font-medium text-text-secondary uppercase tracking-wider mb-1.5">
                Plant Capacity (AC Export)
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={state.capacity_kw / 1000}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    if (val < 0) return;
                    const clamped = Math.max(val, 0.1);
                    const newKw = Math.round(clamped * 1000);
                    setState({ ...state, capacity_kw: newKw, intent: "" });
                  }}
                  placeholder="5.0"
                  step={getCapacityStep(state.capacity_kw / 1000)}
                  min="0"
                  className="w-28 px-3 py-2 border border-border rounded-xl text-[16px] focus:outline-none focus:ring-1 focus:ring-brand/30"
                />
                <span className="text-[15px] text-text-secondary">MW</span>
              </div>
              {(() => {
                const hint = getCapacityHint(state.country, state.capacity_kw);
                return hint ? <p className="text-[12px] text-brand mt-1.5">{hint}</p> : null;
              })()}
            </div>
            <div>
              <label className="block text-[13px] font-medium text-text-secondary uppercase tracking-wider mb-1.5">
                Intended Use
              </label>
              <div className="space-y-2">
                {getIntentOptions(state.country, state.capacity_kw).map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setState({ ...state, intent: opt.value })}
                    className={`w-full text-left px-3 py-2 rounded-xl border transition-colors text-[14px] ${
                      state.intent === opt.value ? "border-brand bg-brand-light font-medium" : "border-border hover:border-border-secondary"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-[13px] font-medium text-text-secondary mb-1">Project location</label>
              <input
                type="text"
                value={state.fields["S-003"] || ""}
                onChange={(e) => updateField("S-003", e.target.value)}
                placeholder={LOCATION_PLACEHOLDER[state.country] || "e.g., project location"}
                className="w-full px-3 py-2 border border-border rounded-xl text-[16px] focus:outline-none focus:ring-1 focus:ring-brand/30"
              />
            </div>
          </div>
        )}

        {/* Step 1: Project Status (all status questions combined) */}
        {step === 1 && (
          <div className="space-y-5">
            <p className="text-[12px] text-text-tertiary">All questions are optional. Answer what you can for a more accurate checkup.</p>
            <div>
              <h3 className="text-[14px] font-medium text-text-primary mb-3">Land</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-[13px] text-text-secondary mb-1">Land tenure type</label>
                  <select value={state.fields["S-010"] || ""} onChange={(e) => updateField("S-010", e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-xl text-[16px] bg-surface focus:outline-none focus:ring-1 focus:ring-brand/30">
                    <option value="">— Select —</option>
                    {[...LAND_TENURE_OPTIONS[state.country], "Not yet secured"].map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[13px] text-text-secondary mb-1">Site area (hectares)</label>
                  <input type="number" value={state.fields["S-005"] || ""} onChange={(e) => updateField("S-005", e.target.value)}
                    placeholder="e.g., 2.5" className="w-full px-3 py-2 border border-border rounded-xl text-[16px] focus:outline-none focus:ring-1 focus:ring-brand/30" />
                </div>
                <div>
                  <label className="block text-[13px] text-text-secondary mb-1">Land title or lease agreement?</label>
                  <select value={state.fields["S-009"] || ""} onChange={(e) => updateField("S-009", e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-xl text-[16px] bg-surface focus:outline-none focus:ring-1 focus:ring-brand/30">
                    <option value="">— Select —</option>
                    {["Yes", "In progress", "No"].map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
              {/* Country-specific land question */}
              {(() => {
                const cq = getCountryQuestions(state.country).find(q => q.section === "land");
                if (!cq) return null;
                return (
                  <div className="mt-3">
                    <label className="block text-[13px] text-text-secondary mb-1">{cq.label}</label>
                    <select value={state.fields[cq.id] || ""} onChange={(e) => updateField(cq.id, e.target.value)}
                      className="w-full px-3 py-2 border border-border rounded-xl text-[16px] bg-surface focus:outline-none focus:ring-1 focus:ring-brand/30">
                      <option value="">— Select —</option>
                      {cq.options.map(o => <option key={o}>{o}</option>)}
                    </select>
                  </div>
                );
              })()}
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <h3 className="text-[14px] font-medium text-text-primary mb-3">Environmental & Grid</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-[13px] text-text-secondary mb-1">EIA study type</label>
                  <select value={state.fields["E-001"] || ""} onChange={(e) => updateField("E-001", e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-xl text-[16px] bg-surface focus:outline-none focus:ring-1 focus:ring-brand/30">
                    <option value="">— Select —</option>
                    {[...EIA_TYPE_OPTIONS[state.country], "Not yet determined"].map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[13px] text-text-secondary mb-1">EIA license status</label>
                  <select value={state.fields["E-005"] || ""} onChange={(e) => updateField("E-005", e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-xl text-[16px] bg-surface focus:outline-none focus:ring-1 focus:ring-brand/30">
                    <option value="">— Select —</option>
                    {["Yes, approved", "Application submitted", "Study in progress", "Not started"].map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[13px] text-text-secondary mb-1">Grid connection agreement?</label>
                  <select value={state.fields["G-003"] || ""} onChange={(e) => updateField("G-003", e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-xl text-[16px] bg-surface focus:outline-none focus:ring-1 focus:ring-brand/30">
                    <option value="">— Select —</option>
                    {["Yes", "Applied", "Not yet"].map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
                {/* Country-specific env/grid question */}
                {(() => {
                  const cq = getCountryQuestions(state.country).find(q => q.section === "env");
                  if (!cq) return null;
                  return (
                    <div>
                      <label className="block text-[13px] text-text-secondary mb-1">{cq.label}</label>
                      <select value={state.fields[cq.id] || ""} onChange={(e) => updateField(cq.id, e.target.value)}
                        className="w-full px-3 py-2 border border-border rounded-xl text-[16px] bg-surface focus:outline-none focus:ring-1 focus:ring-brand/30">
                        <option value="">— Select —</option>
                        {cq.options.map(o => <option key={o}>{o}</option>)}
                      </select>
                    </div>
                  );
                })()}
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <h3 className="text-[14px] font-medium text-text-primary mb-3">Financial</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-[13px] text-text-secondary mb-1">Estimated project cost (USD)</label>
                  <select value={state.fields["F-001"] || ""} onChange={(e) => updateField("F-001", e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-xl text-[16px] bg-surface focus:outline-none focus:ring-1 focus:ring-brand/30">
                    <option value="">— Select —</option>
                    {["Under $500K", "$500K - $2M", "$2M - $10M", "$10M - $50M", "Over $50M"].map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[13px] text-text-secondary mb-1">Expected tariff rate?</label>
                  <select value={state.fields["F-006"] || ""} onChange={(e) => updateField("F-006", e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-xl text-[16px] bg-surface focus:outline-none focus:ring-1 focus:ring-brand/30">
                    <option value="">— Select —</option>
                    {["Yes, agreed with offtaker", "Estimated but not agreed", "Not yet determined"].map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[13px] text-text-secondary mb-1">Funding status</label>
                  <select value={state.fields["F-011"] || ""} onChange={(e) => updateField("F-011", e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-xl text-[16px] bg-surface focus:outline-none focus:ring-1 focus:ring-brand/30">
                    <option value="">— Select —</option>
                    {["Fully self-funded", "Debt financing secured", "Seeking financing", "Grant-funded", "Not yet determined"].map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <h3 className="text-[14px] font-medium text-text-primary mb-3">Offtake</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-[13px] text-text-secondary mb-1">Who is your intended offtaker?</label>
                  <select value={state.fields["O-001"] || ""} onChange={(e) => updateField("O-001", e.target.value)}
                    className="w-full px-3 py-2 border border-border rounded-xl text-[16px] bg-surface focus:outline-none focus:ring-1 focus:ring-brand/30">
                    <option value="">— Select —</option>
                    {OFFTAKER_OPTIONS[state.country].map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
                {/* Country-specific offtake question (e.g., GH ECG creditworthiness) */}
                {(() => {
                  const cq = getCountryQuestions(state.country).find(q => q.section === "offtake");
                  if (!cq) return null;
                  return (
                    <div>
                      <label className="block text-[13px] text-text-secondary mb-1">{cq.label}</label>
                      <select value={state.fields[cq.id] || ""} onChange={(e) => updateField(cq.id, e.target.value)}
                        className="w-full px-3 py-2 border border-border rounded-xl text-[16px] bg-surface focus:outline-none focus:ring-1 focus:ring-brand/30">
                        <option value="">— Select —</option>
                        {cq.options.map(o => <option key={o}>{o}</option>)}
                      </select>
                    </div>
                  );
                })()}
              </div>
            </div>

          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
          {step > 0 ? (
            <button onClick={() => setStep(step - 1)} className="flex items-center gap-1 text-[14px] text-text-secondary hover:text-text-primary">
              <ChevronLeft size={14} /> Back
            </button>
          ) : <div />}

          {step === 0 ? (
            <button
              onClick={() => setStep(1)}
              disabled={!state.capacity_kw}
              className="flex items-center gap-1 px-4 py-1.5 bg-brand text-white rounded-xl text-[16px] font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next <ChevronRight size={14} />
            </button>
          ) : (
            <button onClick={handleGenerate} className="flex items-center gap-1.5 px-4 py-1.5 bg-brand text-white rounded-xl text-[16px] font-medium">
              <Shield size={14} /> Run Checkup
            </button>
          )}
        </div>
      </div>

      {!isEmbed && (
        <p className="text-[12px] text-text-tertiary text-center mt-4">
          Free regulatory checkup. No account required. No personal data collected.
        </p>
      )}
    </div>
  );
}

// ─── Country Selector with horizontal scroll ───

function CountrySelector({ countries, selected, onSelect }: {
  countries: CountryCode[];
  selected: CountryCode;
  onSelect: (code: CountryCode) => void;
}) {
  return (
    <div>
      <label className="block text-[13px] font-medium text-text-secondary uppercase tracking-wider mb-2">
        Country
      </label>

      {/* Horizontally scrollable country cards */}
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide scroll-smooth snap-x snap-mandatory">
        {countries.map((code) => (
          <button
            key={code}
            onClick={() => onSelect(code)}
            className={`flex-shrink-0 flex flex-col items-center gap-1 w-[130px] p-2.5 rounded-xl border transition-colors snap-start ${
              selected === code ? "border-brand bg-brand-light" : "border-border hover:border-border-secondary"
            }`}
          >
            <span className="text-xl">{COUNTRY_META[code].flag}</span>
            <span className="text-[13px] font-medium">{COUNTRY_META[code].name}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function CheckupHeader({ country }: { country?: CountryCode }) {
  const countryName = country ? COUNTRY_META[country].name : "your target market";
  return (
    <div className="mb-6">
      <span className="text-[22px] font-bold" style={{ letterSpacing: "-0.02em" }}>Regulatory Checkup</span>
      <p className="text-[14px] text-text-secondary mt-1">
        Find out which licenses and permits your solar project needs in {countryName}, and see where you stand.
      </p>
    </div>
  );
}

// ─── Results ───

const DIMENSION_ICONS: Record<string, any> = {
  "Technical": Zap, "Environmental": Leaf, "Financial": TrendingUp, "Legal / Land": Landmark, "Regulatory": Shield,
};

const CRITICALITY_LABELS: Record<string, string> = {
  critical_path: "Critical path", high: "High priority", medium: "Medium", low: "Low",
};

function CheckupResults({ result, state, email, emailSubmitted, onEmailChange, onEmailSubmit, onStartOver, isEmbed, postToParent }: {
  result: EvaluationOutput; state: CheckupState; email: string; emailSubmitted: boolean;
  onEmailChange: (v: string) => void; onEmailSubmit: () => void; onStartOver: () => void;
  isEmbed?: boolean; postToParent?: (type: string, data?: Record<string, any>) => void;
}) {
  const topNudge = result.nudges[0];
  // Shorten title for summary card
  const topPriorityValue = topNudge
    ? topNudge.title.replace(/\s*—.*/, "").slice(0, 25)
    : "All clear";
  const topPrioritySub = topNudge
    ? `${CRITICALITY_LABELS[topNudge.criticality] || topNudge.criticality} · ~${topNudge.effort}`
    : "No actions needed";

  return (
    <div className={`${isEmbed ? "px-2 py-4" : "max-w-3xl mx-auto px-4 py-8"}`}>
      {!isEmbed && <style dangerouslySetInnerHTML={{ __html: PRINT_STYLES }} />}
      {!isEmbed && <CheckupHeader country={state.country} />}

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <SummaryCard label="Licenses Required" value={String(result.licenses_total)} sub={`for ${(state.capacity_kw / 1000).toFixed(1)} MW in ${COUNTRY_META[state.country].name}`} />
        <SummaryCard label="Readiness" value={`${result.readiness_pct}%`} sub="based on your answers" />
        <SummaryCard label="Bankability" value={result.bankability.grade.replace(/_/g, " ")} sub={`${result.bankability.overall_score}% score`} />
        <SummaryCard label="Top Priority" value={topPriorityValue} sub={topPrioritySub} />
      </div>

      {/* License roadmap */}
      <section className="bg-surface border border-border rounded-2xl p-5 mb-4">
        <h2 className="text-[17px] font-medium mb-3">Your License Roadmap</h2>
        <p className="text-[13px] text-text-tertiary mb-4">
          A {(state.capacity_kw / 1000).toFixed(1)} MW solar project in {COUNTRY_META[state.country].name} requires these {result.licenses_total} licenses and permits.
        </p>
        <div className="space-y-2">
          {result.per_license_readiness.map((lic) => {
            const accentColor = lic.readiness_pct >= 75 ? "var(--brand)"
              : lic.readiness_pct > 0 ? "var(--warning)"
              : "var(--border)";
            const fillBg = lic.readiness_pct >= 75 ? "#ecfdf5"
              : lic.readiness_pct > 0 ? "#fffbeb"
              : "transparent";
            return (
              <div key={lic.license_type_id} className="relative overflow-hidden rounded-xl bg-surface-secondary">
                {/* Left accent stripe — always visible, colored by readiness tier */}
                <div className="absolute inset-y-0 left-0 w-1 rounded-l-xl" style={{ backgroundColor: accentColor }} />
                {/* Progress fill — extends from left */}
                {lic.readiness_pct > 0 && (
                  <div
                    className="absolute inset-y-0 left-0"
                    style={{ width: `${lic.readiness_pct}%`, backgroundColor: fillBg }}
                  />
                )}
                {/* Content */}
                <div className="relative flex items-center gap-3 p-3 pl-4">
                  <span className="flex-shrink-0 text-[14px] font-semibold w-10 text-right" style={{ color: accentColor }}>
                    {lic.readiness_pct}%
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[14px] font-medium truncate">{lic.license_name}</div>
                    <div className="text-[12px] text-text-tertiary">{lic.authority}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Compliance summary */}
      {result.compliance_summary && result.compliance_summary.total_obligations > 0 && (
        <section className="bg-surface border border-border rounded-2xl p-5 mb-4">
          <h2 className="text-[17px] font-medium mb-3">Compliance Obligations</h2>
          <p className="text-[13px] text-text-tertiary mb-4">
            {COUNTRY_META[state.country].name} has {result.compliance_summary.total_obligations} regulatory compliance requirements for solar projects.
          </p>
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="bg-danger-light rounded-xl p-2.5 text-center">
              <div className="text-[18px] font-medium text-danger">{result.compliance_summary.gate_tier_count}</div>
              <div className="text-[11px] text-text-tertiary uppercase tracking-wider">Gate</div>
            </div>
            <div className="bg-warning-light rounded-xl p-2.5 text-center">
              <div className="text-[18px] font-medium text-warning">{result.compliance_summary.pre_operational_count}</div>
              <div className="text-[11px] text-text-tertiary uppercase tracking-wider">Pre-operational</div>
            </div>
            <div className="bg-info-light rounded-xl p-2.5 text-center">
              <div className="text-[18px] font-medium text-info">{result.compliance_summary.ongoing_count}</div>
              <div className="text-[11px] text-text-tertiary uppercase tracking-wider">Ongoing</div>
            </div>
          </div>
          {result.compliance_summary.key_gates.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-[14px] font-medium">Key Gate Requirements</h3>
              {result.compliance_summary.key_gates.map((gate) => (
                <div key={gate.display_name} className="p-2.5 bg-surface-secondary rounded-xl">
                  <div className="text-[14px] font-medium">{gate.display_name}</div>
                  <div className="text-[12px] text-text-tertiary">
                    {gate.approval_body}{gate.approval_timeline ? ` · ${gate.approval_timeline}` : ""}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Email gate */}
      {!emailSubmitted ? (
        <section className="bg-ai-light border border-ai/20 rounded-2xl p-6 mb-4 print-hide">
          <div className="flex items-start gap-3">
            <Lock size={20} className="text-ai flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h2 className="text-[17px] font-medium mb-1">Get Your Full Report</h2>
              <p className="text-[14px] text-text-secondary mb-4">
                Enter your email to see detailed recommendations, bankability scoring across 5 dimensions, and the questions investors will ask about your project.
              </p>
              <div className="flex gap-2">
                <input type="email" value={email} onChange={(e) => onEmailChange(e.target.value)}
                  placeholder="you@company.com"
                  className="flex-1 px-3 py-2 border border-border rounded-xl text-[16px] focus:outline-none focus:ring-1 focus:ring-ai/30" />
                <button onClick={onEmailSubmit} disabled={!email || !email.includes("@")}
                  className="flex items-center gap-1.5 px-4 py-2 bg-ai text-white rounded-xl text-[14px] font-medium disabled:opacity-40 disabled:cursor-not-allowed">
                  <Mail size={14} /> Unlock
                </button>
              </div>
              <p className="text-[12px] text-text-tertiary mt-2">We'll send you a copy. No spam, unsubscribe anytime.</p>
            </div>
          </div>
        </section>
      ) : (
        <>
          {/* Recommendations */}
          {result.nudges.length > 0 && (
            <section className="bg-surface border border-border rounded-2xl p-5 mb-4">
              <h2 className="text-[17px] font-medium mb-3">Recommendations</h2>
              <div className="space-y-3">
                {result.nudges.slice(0, 5).map((nudge) => <NudgeCard key={nudge.id} nudge={nudge} />)}
              </div>
            </section>
          )}

          {/* Documents You'll Need */}
          {result.required_documents && result.required_documents.length > 0 && (() => {
            const submissions = result.required_documents.filter(d => d.is_submission);
            const supporting = result.required_documents.filter(d => !d.is_submission);
            return (
              <section className="bg-surface border border-border rounded-2xl p-5 mb-4">
                <h2 className="text-[17px] font-medium mb-1">Documents You'll Need</h2>
                <p className="text-[13px] text-text-tertiary mb-4">
                  Based on your {result.licenses_total} required licenses, prepare these {result.required_documents.length} documents.
                </p>
                {submissions.length > 0 && (
                  <div className="mb-3">
                    <h3 className="text-[13px] font-medium text-text-secondary uppercase tracking-wider mb-2">Submission Documents</h3>
                    <p className="text-[12px] text-text-tertiary mb-2">Filed with your license applications</p>
                    <div className="space-y-1.5">
                      {submissions.map((doc) => (
                        <div key={doc.doc_type} className="flex items-start gap-2 p-2.5 bg-surface-secondary rounded-xl">
                          <FileText size={14} className="text-brand flex-shrink-0 mt-0.5" />
                          <div className="min-w-0">
                            <div className="text-[14px] font-medium">{doc.display_name}</div>
                            <div className="text-[12px] text-text-tertiary">Required for: {doc.license_names.join(", ")}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {supporting.length > 0 && (
                  <div>
                    <h3 className="text-[13px] font-medium text-text-secondary uppercase tracking-wider mb-2">Supporting Documents</h3>
                    <p className="text-[12px] text-text-tertiary mb-2">Support your applications but aren't formally submitted</p>
                    <div className="space-y-1.5">
                      {supporting.map((doc) => (
                        <div key={doc.doc_type} className="flex items-start gap-2 p-2.5 bg-surface-secondary rounded-xl">
                          <FileText size={14} className="text-text-tertiary flex-shrink-0 mt-0.5" />
                          <div className="min-w-0">
                            <div className="text-[14px] font-medium">{doc.display_name}</div>
                            <div className="text-[12px] text-text-tertiary">For: {doc.license_names.join(", ")}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            );
          })()}

          {/* Bankability */}
          <section className="bg-surface border border-border rounded-2xl p-5 mb-4">
            <h2 className="text-[17px] font-medium mb-1">Bankability Assessment</h2>
            <p className="text-[13px] text-text-tertiary mb-4">How ready is your project for investor due diligence?</p>
            <div className="space-y-3">
              {result.bankability.dimensions.map((dim) => {
                const Icon = DIMENSION_ICONS[dim.dimension] || Shield;
                return (
                  <div key={dim.dimension} className="flex items-center gap-3">
                    <Icon size={16} className="text-text-tertiary flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[14px] font-medium">{dim.dimension}</span>
                        <span className="text-[13px] text-text-secondary">{dim.score}%</span>
                      </div>
                      <div className="w-full h-1.5 bg-surface-tertiary rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{
                          width: `${dim.score}%`,
                          backgroundColor: dim.score >= 75 ? "var(--brand)" : dim.score >= 50 ? "var(--info)" : dim.score >= 25 ? "var(--warning)" : "var(--danger)",
                        }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {result.bankability.investor_questions.length > 0 && (
              <div className="mt-4 pt-3 border-t border-border">
                <h3 className="text-[14px] font-medium mb-2">Questions Investors Will Ask</h3>
                <ul className="space-y-1.5">
                  {result.bankability.investor_questions.map((q, i) => (
                    <li key={i} className="text-[13px] text-text-secondary flex gap-2">
                      <span className="text-text-tertiary">•</span>{q}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        </>
      )}

      {/* CTA */}
      <section className="bg-brand-light border border-brand/20 rounded-2xl p-6 text-center mb-4 print-hide">
        <h2 className="text-[17px] font-medium text-brand mb-2">Ready to manage your compliance?</h2>
        <p className="text-[14px] text-text-secondary mb-4">
          enereg tracks all {result.fields_total} regulatory fields, extracts data from your documents, and generates pre-filled submission templates.
        </p>
        <a
          href={(() => {
            const params = checkupToken ? `?from=checkup&checkup_token=${checkupToken}` : "?from=checkup";
            return isEmbed ? `https://app.enereg.com/login${params}` : `/login${params}`;
          })()}
          target={isEmbed ? "_blank" : undefined}
          rel={isEmbed ? "noopener noreferrer" : undefined}
          onClick={() => postToParent?.("enereg-checkup-cta", { action: "signup" })}
          className="inline-flex items-center gap-1.5 px-5 py-2 bg-brand text-white rounded-xl text-[16px] font-medium hover:opacity-90 transition-opacity"
        >
          Sign up for enereg <ArrowRight size={16} />
        </a>
      </section>

      <div className="flex items-center justify-between print-hide">
        <button onClick={onStartOver} className="text-[14px] text-text-secondary hover:text-text-primary">Run another checkup</button>
        {!isEmbed && (
          <button onClick={() => window.print()} className="flex items-center gap-1.5 text-[14px] text-text-secondary hover:text-text-primary">
            <FileText size={14} /> Print / Save as PDF
          </button>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="bg-surface border border-border rounded-xl p-3 text-center">
      <div className="text-[11px] text-text-tertiary uppercase tracking-wider">{label}</div>
      <div className="text-[11px] text-text-tertiary mb-1">{sub}</div>
      <div className="text-[18px] font-medium capitalize leading-tight">{value}</div>
    </div>
  );
}

const CRITICALITY_STYLES = {
  critical_path: { bg: "bg-danger-light", text: "text-danger", label: "Critical Path" },
  high: { bg: "bg-warning-light", text: "text-warning", label: "High" },
  medium: { bg: "bg-info-light", text: "text-info", label: "Medium" },
  low: { bg: "bg-surface-tertiary", text: "text-text-secondary", label: "Low" },
};

function NudgeCard({ nudge }: { nudge: Nudge }) {
  const style = CRITICALITY_STYLES[nudge.criticality];
  return (
    <div className="p-3 bg-surface-secondary rounded-xl">
      <div className="flex items-start gap-2 mb-1">
        <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${style.bg} ${style.text}`}>{style.label}</span>
        <span className="text-[11px] text-text-tertiary">~{nudge.effort}</span>
      </div>
      <h3 className="text-[14px] font-medium mb-1">{nudge.title}</h3>
      <p className="text-[13px] text-text-secondary">{nudge.body}</p>
      <p className="text-[12px] text-brand mt-1">{nudge.impact}</p>
    </div>
  );
}
